import { Typography } from '@equinor/eds-core-react'
import AsyncResource from '../../components/async-resource/async-resource'
import { Breadcrumb } from '../../components/breadcrumb'
import { StreamingLog } from '../../components/code/log'
import { EventsList } from '../../components/events-list'
import { ReplicaOverview } from '../../components/replica/replica-overview'
import { replicaEventListExpandedKey, useLocalStorage } from '../../hooks/use-local-storage'
import { routes } from '../../router/routes'
import { pollingInterval } from '../../store/defaults'
import { getReplicaLogStreamUrl } from '../../store/eventstream-log-api'
import { radixApi, useGetEnvironmentQuery, useGetReplicaEventsQuery } from '../../store/radix-api'
import { withRouteParams } from '../../utils/router'
import { getEnvsUrl } from '../../utils/routing'
import { dataSorter, sortCompareDate } from '../../utils/sort-utils'
import { routeWithParams, smallReplicaName } from '../../utils/string'

interface Props {
  appName: string
  envName: string
  componentName: string
  replicaName: string
}

function ReplicaPage({ appName, envName, componentName, replicaName }: Props) {
  const environmentState = useGetEnvironmentQuery({ appName, envName }, { skip: !appName || !envName, pollingInterval })
  const [getLog] = radixApi.endpoints.replicaLog.useLazyQuery()
  const eventStreamUrl = getReplicaLogStreamUrl(appName, envName, componentName, replicaName)

  const replica = environmentState.data?.activeDeployment?.components
    ?.find((x) => x.name === componentName)
    ?.replicaList?.find((x) => x.name === replicaName)

  const [isEventListExpanded, setIsEventListExpanded] = useLocalStorage(replicaEventListExpandedKey, false)
  const { data: events } = useGetReplicaEventsQuery(
    { appName, envName, componentName, podName: replicaName },
    {
      skip: !appName || !envName || !componentName || !replicaName || !isEventListExpanded,
      pollingInterval,
    }
  )

  return (
    <>
      <Breadcrumb
        links={[
          { label: appName, to: routeWithParams(routes.app, { appName }) },
          { label: 'Environments', to: getEnvsUrl(appName) },
          {
            label: envName,
            to: routeWithParams(routes.appEnvironment, { appName, envName }),
          },
          {
            label: componentName,
            to:
              replica?.type === 'JobManager' || replica?.type === 'JobManagerAux'
                ? routeWithParams(routes.appActiveJobComponent, {
                    appName,
                    envName,
                    jobComponentName: componentName,
                  })
                : routeWithParams(routes.appActiveComponent, {
                    appName,
                    envName,
                    componentName,
                  }),
          },
          { label: smallReplicaName(replicaName) },
        ]}
      />
      <AsyncResource asyncState={environmentState}>
        {replica && (
          <>
            <Typography variant="h4">Overview</Typography>
            <ReplicaOverview replica={replica} />
            <StreamingLog
              eventStreamUrl={eventStreamUrl}
              copy
              download
              downloadCb={() =>
                getLog({ appName, envName, componentName, podName: replicaName, lines: '100000000' }).unwrap()
              }
            />
          </>
        )}
      </AsyncResource>
      <EventsList
        isExpanded={isEventListExpanded}
        onExpanded={setIsEventListExpanded}
        events={dataSorter(events ?? [], [
          ({ lastTimestamp: x }, { lastTimestamp: y }) => sortCompareDate(x, y, 'descending'),
        ])}
      />
    </>
  )
}

export default withRouteParams(ReplicaPage)
